import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  Clock,
  TrendingUp,
  Calendar,
  ExternalLink
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'

interface UptimeIndicatorProps {
  showViewDetailsButton?: boolean
}

interface TimelinePoint {
  periodStart: string
  periodEnd: string
  overallStatus: 'operational' | 'degraded' | 'outage' | 'maintenance'
  uptimePercent: number
  monitorsDown: number
  monitorsTotal: number
  avgResponseTime: number | null
}

interface TimelineResponse {
  timeline: TimelinePoint[]
  currentStatus: {
    overallStatus: 'operational' | 'degraded' | 'outage' | 'maintenance'
    uptimePercentage: number
    totalIncidents24h: number
    avgResponseTime: number
    monitorsTotal: number
    monitorsUp: number
    monitorsDown: number
    monitorsPending: number
  }
}

const UptimeIndicator = ({ showViewDetailsButton = true }: UptimeIndicatorProps) => {
  const [timelineData, setTimelineData] = useState<TimelineResponse | null>(null)
  const [monitorsData, setMonitorsData] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [animatedUptime, setAnimatedUptime] = useState(0)
  const navigate = useNavigate()

  // Fetch timeline data
  useEffect(() => {
    const fetchTimelineData = async () => {
      try {
        setIsLoading(true)
        
        // First, trigger data collection (with throttling)
        const lastCollection = localStorage.getItem('lastUptimeCollection')
        const now = Date.now()
        const fiveMinutesAgo = now - (5 * 60 * 1000)
        
        if (!lastCollection || parseInt(lastCollection) < fiveMinutesAgo) {
          try {
            await fetch('/api/uptime/collect', { method: 'POST' })
            localStorage.setItem('lastUptimeCollection', now.toString())
            // Data collection triggered
          } catch (collectError) {
            console.warn('Failed to trigger collection:', collectError)
          }
        }
        
        // Fetch both timeline and monitors data in parallel
        const [timelineResponse, monitorsResponse] = await Promise.all([
          fetch('/api/uptime/timeline'),
          fetch('/api/uptime/monitors')
        ])
        
        if (!timelineResponse.ok) {
          throw new Error(`Timeline API error! status: ${timelineResponse.status}`)
        }
        
        const timelineData: TimelineResponse = await timelineResponse.json()
        setTimelineData(timelineData)
        
        // Fetch monitors data for accurate uptime percentage
        if (monitorsResponse.ok) {
          const monitorsData = await monitorsResponse.json()
          setMonitorsData(monitorsData)
        } else {
          console.warn('Failed to fetch monitors data, using timeline data fallback')
        }
      } catch (error) {
        console.error('Failed to fetch timeline data:', error)
        // Fallback to empty data structure
        setTimelineData({
          timeline: [],
          currentStatus: {
            overallStatus: 'operational',
            uptimePercentage: 100,
            totalIncidents24h: 0,
            avgResponseTime: 0,
            monitorsTotal: 0,
            monitorsUp: 0,
            monitorsDown: 0,
            monitorsPending: 0
          }
        })
      } finally {
        setIsLoading(false)
      }
    }

    fetchTimelineData()
    
    // Refresh data every 5 minutes
    const interval = setInterval(fetchTimelineData, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  // Animate uptime counter - use monitors data for accuracy, fallback to timeline
  useEffect(() => {
    const actualUptimeData = monitorsData || timelineData?.currentStatus
    if (actualUptimeData) {
      const timer = setInterval(() => {
        setAnimatedUptime(prev => {
          const target = monitorsData ? monitorsData.uptimePercentage : timelineData?.currentStatus.uptimePercentage || 100
          const step = target / 50
          return prev < target ? Math.min(prev + step, target) : target
        })
      }, 20)

      return () => clearInterval(timer)
    }
  }, [timelineData, monitorsData])

  // Convert timeline data to the format expected by the component
  const uptimeData = timelineData?.timeline.map(point => ({
    date: new Date(point.periodStart),
    uptime: point.uptimePercent,
    status: point.uptimePercent >= 99.5 ? 'excellent' : 
            point.uptimePercent >= 98 ? 'good' : 
            point.uptimePercent >= 85 ? 'degraded' : 'poor'
  })) || []

  const getStatusConfig = () => {
    if (!timelineData) return null

    const configs = {
      operational: {
        text: 'All Systems Operational',
        color: 'from-green-500 to-emerald-500',
        icon: <CheckCircle className="w-5 h-5" />,
        bgColor: 'bg-green-500/10 dark:bg-green-500/20',
        borderColor: 'border-green-400/30 dark:border-green-400/40'
      },
      degraded: {
        text: 'Partially Degraded Service',
        color: 'from-yellow-500 to-orange-500',
        icon: <AlertTriangle className="w-5 h-5" />,
        bgColor: 'bg-yellow-500/10 dark:bg-yellow-500/20',
        borderColor: 'border-yellow-400/30 dark:border-yellow-400/40'
      },
      outage: {
        text: 'Service Outage',
        color: 'from-red-500 to-pink-500',
        icon: <XCircle className="w-5 h-5" />,
        bgColor: 'bg-red-500/10 dark:bg-red-500/20',
        borderColor: 'border-red-400/30 dark:border-red-400/40'
      },
      maintenance: {
        text: 'Scheduled Maintenance',
        color: 'from-blue-500 to-cyan-500',
        icon: <Clock className="w-5 h-5" />,
        bgColor: 'bg-blue-500/10 dark:bg-blue-500/20',
        borderColor: 'border-blue-400/30 dark:border-blue-400/40'
      }
    }

    return configs[timelineData.currentStatus.overallStatus]
  }

  const getDayStatusColor = (status: string) => {
    switch (status) {
      case 'excellent': return 'bg-green-500'
      case 'good': return 'bg-green-400' 
      case 'degraded': return 'bg-yellow-500'
      case 'poor': return 'bg-red-500'
      default: return 'bg-gray-400'
    }
  }

  const getStatusColorClass = (status: string) => {
    switch (status) {
      case 'excellent': return 'text-green-600 dark:text-green-400'
      case 'good': return 'text-green-600 dark:text-green-400'
      case 'degraded': return 'text-yellow-600 dark:text-yellow-400'
      case 'poor': return 'text-red-600 dark:text-red-400'
      default: return 'text-gray-600 dark:text-gray-400'
    }
  }

  if (isLoading) {
    return (
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/60 dark:border-gray-700/60 rounded-2xl p-6 hover:shadow-lg hover:border-gray-300/80 dark:hover:border-gray-600/80 transition-shadow transition-border duration-300" style={{ minHeight: '380px' }}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-32 mb-4"></div>
          <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded w-20 mb-4"></div>
          <div className="flex gap-1 mb-6">
            {[...Array(24)].map((_, i) => (
              <div key={i} className="h-10 w-3 bg-gray-300 dark:bg-gray-600 rounded-sm"></div>
            ))}
          </div>
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="h-16 bg-gray-300 dark:bg-gray-600 rounded-xl"></div>
            <div className="h-16 bg-gray-300 dark:bg-gray-600 rounded-xl"></div>
            <div className="h-16 bg-gray-300 dark:bg-gray-600 rounded-xl"></div>
            <div className="h-16 bg-gray-300 dark:bg-gray-600 rounded-xl"></div>
          </div>
          <div className="h-12 bg-gray-300 dark:bg-gray-600 rounded-xl"></div>
        </div>
      </div>
    )
  }

  const statusConfig = getStatusConfig()

  // Use the real system status from timeline data
  const currentOverallStatus = timelineData?.currentStatus.overallStatus || 'operational'

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/60 dark:border-gray-700/60 rounded-2xl p-6 hover:shadow-lg hover:border-gray-300/80 dark:hover:border-gray-600/80 transition-shadow transition-border duration-300"
    >
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
            Demo Lab Infrastructure
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Real-time performance over the past 24 hours
          </p>
        </div>
        
        {statusConfig && (
          <div className={`flex items-center gap-3 px-4 py-2 rounded-full ${statusConfig.bgColor} border ${statusConfig.borderColor}`}>
            <div className={`w-3 h-3 rounded-full ${
              currentOverallStatus === 'operational' ? 'bg-green-500' :
              currentOverallStatus === 'degraded' ? 'bg-yellow-500' :
              currentOverallStatus === 'outage' ? 'bg-red-500' :
              currentOverallStatus === 'maintenance' ? 'bg-blue-500' : 'bg-gray-400'
            }`} />
            <span className={`text-sm font-medium bg-gradient-to-r ${statusConfig.color} bg-clip-text text-transparent`}>
              {statusConfig.text}
            </span>
          </div>
        )}
      </div>

      {/* 90-Day Uptime Bars - Horizontal Layout */}
      <div className="mb-6 overflow-visible">
        <div className="flex flex-wrap gap-0.5 sm:gap-1 mb-4 overflow-visible">
          {uptimeData.map((day, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.01, duration: 0.3 }}
              className="group relative overflow-visible"
            >
              <div
                className={`w-2 sm:w-3 h-8 sm:h-10 ${getDayStatusColor(day.status)} rounded-sm cursor-pointer transition-transform hover:scale-110 hover:brightness-110`}
                title={`${day.date.toLocaleTimeString()}: ${day.uptime.toFixed(1)}% uptime`}
              />
              {/* Tooltip - Moved outside bar for better Safari rendering */}
              <div 
                className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none shadow-xl border border-gray-700 dark:border-gray-600"
                style={{ zIndex: 9999, transform: 'translateX(-50%) translateZ(0)' }}
              >
                <div className="font-medium">{day.date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</div>
                <div className="text-gray-300">{day.uptime.toFixed(1)}% uptime</div>
                {/* Arrow */}
                <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                  <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Timeline Labels */}
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>24 hours ago</span>
          <span className="font-semibold">
            <motion.span
              className="text-lg font-bold text-gray-900 dark:text-white"
              style={{ minWidth: '4.5ch', display: 'inline-block' }}
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5 }}
            >
              {animatedUptime.toFixed(2)}%
            </motion.span>
            <span className="ml-1">uptime</span>
          </span>
          <span>Now</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="text-center p-3 bg-gray-50/80 dark:bg-gray-700/30 rounded-xl">
          <div className="text-lg font-bold text-gray-900 dark:text-white">
            {timelineData?.currentStatus.monitorsTotal || 0}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
            Services
          </div>
        </div>
        
        <div className="text-center p-3 bg-gray-50/80 dark:bg-gray-700/30 rounded-xl">
          <div className="text-lg font-bold text-gray-900 dark:text-white">
            {uptimeData.filter(d => d.status === 'excellent' || d.status === 'good').length}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
            Good Periods
          </div>
        </div>
        
        <div className="text-center p-3 bg-gray-50/80 dark:bg-gray-700/30 rounded-xl">
          <div className="text-lg font-bold text-gray-900 dark:text-white">
            {timelineData?.currentStatus.avgResponseTime.toFixed(0) || 0}ms
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
            Avg Response
          </div>
        </div>
        
        <div className="text-center p-3 bg-gray-50/80 dark:bg-gray-700/30 rounded-xl">
          <div className="text-lg font-bold text-gray-900 dark:text-white">
            {timelineData?.currentStatus.totalIncidents24h || 0}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
            Issues
          </div>
        </div>
      </div>

      {/* View Details Button */}
      {showViewDetailsButton && (
        <motion.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          onClick={() => navigate('/lab-status')}
          className="w-full px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-xl transition-colors duration-200 flex items-center justify-center gap-2"
        >
          View Detailed Status
          <ExternalLink className="w-4 h-4" />
        </motion.button>
      )}
    </motion.div>
  )
}

export default UptimeIndicator